import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { computeCompletion } from "@/pages/dashboard/EditorSections";
import { shareGuide } from "@/lib/share";
import { MapPin, Eye, ExternalLink, Share2, Pencil, MoreVertical, Trash2, Plus, Copy as CopyIcon, Sparkles } from "lucide-react";
import { DEMO } from "@/lib/demoImages";
import { toast } from "sonner";

export function PropertyCard({ p, onDeleted, onDuplicated }) {
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const cover = mediaUrl(p.branding?.cover) || DEMO.emptyState;
  const published = p.status === "published";
  const publicUrl = `${window.location.origin}/g/${p.slug}`;
  const { percent } = computeCompletion(p);

  const del = async () => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    await api.delete(`/properties/${p.id}`);
    toast.success("Struttura eliminata");
    onDeleted?.(p.id);
  };

  const duplicate = async () => {
    setMenu(false); setBusy(true);
    try {
      const { data } = await api.post(`/properties/${p.id}/duplicate`);
      toast.success("Struttura duplicata");
      onDuplicated?.(data.property);
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile duplicare"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`property-card-${p.id}`} className="rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-sm">
      <button onClick={() => navigate(`/dashboard/properties/${p.id}`)} className="block w-full text-left">
        <div className="relative h-44">
          <img src={cover} alt={p.name} className="w-full h-full object-cover" />
          <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${published ? "bg-emerald-500 text-white" : "bg-white/90 text-slate-600"}`}>● {published ? "Pubblicata" : "Bozza"}</span>
        </div>
      </button>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-bold text-lg text-slate-900 leading-tight truncate">{p.name}</h3>
            <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{p.basic?.city || "Posizione da definire"}</p>
          </div>
          <div className="relative">
            <button onClick={() => setMenu((m) => !m)} className="p-1.5 text-slate-400"><MoreVertical className="w-4 h-4" /></button>
            {menu && <div className="absolute right-0 mt-1 w-40 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-10" onMouseLeave={() => setMenu(false)}>
              <button onClick={duplicate} data-testid={`property-duplicate-${p.id}`} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"><CopyIcon className="w-4 h-4" /> Duplica</button>
              <button onClick={del} data-testid={`property-delete-${p.id}`} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /> Elimina</button>
            </div>}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {p.views || 0} visite</span>
          <span>·</span>
          <span>Guida completata {percent}%</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${percent}%`, background: percent === 100 ? "#10b981" : "#3B82F6" }} /></div>
        <div className="mt-4 flex items-center gap-2">
          <button onClick={() => navigate(`/dashboard/properties/${p.id}`)} data-testid={`edit-guide-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white text-sm font-semibold rounded-xl py-2.5"><Pencil className="w-3.5 h-3.5" /> Gestisci</button>
          {published && <>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="p-2.5 rounded-xl border border-slate-200 text-slate-600"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={() => shareGuide(publicUrl, p.name)} data-testid={`share-${p.id}`} className="p-2.5 rounded-xl border border-slate-200 text-slate-600"><Share2 className="w-4 h-4" /></button>
          </>}
        </div>
      </div>
    </div>
  );
}

export function EmptyProperties() {
  return (
    <div className="mt-6 rounded-3xl overflow-hidden border border-slate-100 bg-white">
      <img src={DEMO.cover} alt="" className="w-full h-40 object-cover" />
      <div className="p-6 text-center">
        <p className="font-display font-bold text-lg text-slate-900">Nessuna struttura ancora</p>
        <p className="text-slate-500 mt-1">Creiamo la tua prima guida per gli ospiti.</p>
        <Link to="/dashboard/properties/new" className="mt-5 inline-flex items-center gap-2 bg-blue-500 text-white font-semibold rounded-full px-5 py-3"><Plus className="w-4 h-4" /> Crea la tua prima struttura</Link>
      </div>
    </div>
  );
}

export function DemoCard({ compact }) {
  const navigate = useNavigate();
  return (
    <div className={`rounded-3xl overflow-hidden border ${compact ? "border-slate-100 bg-white" : "border-amber-200 bg-amber-50"}`} data-testid="demo-card">
      {!compact && <img src={DEMO.cover} alt="" className="w-full h-32 object-cover" />}
      <div className="p-4">
        <div className="flex items-center gap-2"><span className="text-[10px] font-bold bg-amber-400 text-amber-950 rounded-full px-2 py-0.5">DEMO</span><p className="font-display font-bold text-slate-900">Struttura di esempio</p></div>
        <p className="text-sm text-slate-500 mt-1">{compact ? "Rivedi come appare una guida completa." : "Scopri come può apparire una Guest Guide completa prima di creare la tua."}</p>
        <div className="mt-3 flex gap-2">
          <button onClick={() => navigate("/dashboard/demo")} data-testid="demo-open" className="flex-1 rounded-full bg-blue-500 text-white text-sm font-semibold py-2.5">Apri demo</button>
          <a href="/g/demo-esempio" target="_blank" rel="noreferrer" data-testid="demo-guest-preview" className="rounded-full border border-slate-200 text-slate-600 text-sm font-semibold py-2.5 px-4">Vedi come ospite</a>
        </div>
      </div>
    </div>
  );
}

export default function Properties() {
  const [props, setProps] = useState(null);
  useEffect(() => { api.get("/properties").then(({ data }) => setProps(data.properties)); }, []);

  return (
    <div className="px-4 pt-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Strutture</h1>
        <Link to="/dashboard/properties/new" data-testid="properties-create" className="text-sm font-semibold text-blue-600">+ Aggiungi</Link>
      </div>
      {!props ? <div className="py-20 grid place-items-center"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
        : props.length ? (
          <div className="mt-5 space-y-4">
            {props.map((p) => <PropertyCard key={p.id} p={p} onDeleted={(id) => setProps((x) => x.filter((y) => y.id !== id))} onDuplicated={(np) => setProps((x) => [np, ...x])} />)}
            <div className="pt-2"><p className="text-xs font-semibold text-slate-400 mb-2">Scopri StayGuide</p><DemoCard compact /></div>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="welcome-banner">
              <p className="font-display font-bold text-lg text-slate-900">Benvenuto in StayGuide 👋</p>
              <p className="text-slate-500 mt-1 text-sm">Abbiamo preparato una struttura di esempio per mostrarti come funziona. Esplorala oppure crea subito la tua prima guida.</p>
            </div>
            <DemoCard />
            <Link to="/dashboard/properties/new" data-testid="create-first" className="flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white font-semibold py-3.5"><Plus className="w-4 h-4" /> Crea la mia struttura</Link>
          </div>
        )}
    </div>
  );
}
